import { GridColDef, GridRowSelectionModel, GridValidRowModel } from "@mui/x-data-grid";
import { PaymentDetail } from "../../../../../interfaces/Purchases/Payments/payments";
import { useDialogColumnsAuthorization } from "./columns";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormatNumber } from "../../../../../hooks/useFormatNumber";
import { useParamsFilter } from "../../../../../hooks/useParamsFilter";
import { useDates } from "../../../../../hooks/useDates";
import { AlertColor, Box, Button, Tooltip, tooltipClasses } from "@mui/material";
import GenericPromises from "../../../../../api/GenericPromises";
import { Menuitem } from "../../../../../interfaces/Security/menu";
import { Spinner } from "../../../../../components/Commons/Spinner/Spinner";
import { useGridPreferences } from "../../../../../hooks/useGridPreferences";
import { usePreferenceTypesStore } from "../../../../../store/Preferences/PreferencesStore";
import { PreferenceType } from "../../../../../interfaces/Commons/Preferences/preferences";
import { AxiosError } from "axios";
import { useCurrencies } from "../../../../../hooks/useCurrencies";
import DataTableSticky from "../../../../../components/Tables/GridTableMaterialUI/DataTableSticky";

type TableAuthorizationPayment = {
    resourceScreen: Menuitem | undefined,
    setSelectedRows: Dispatch<SetStateAction<readonly GridValidRowModel[]>>,
    paymentsDetailsData: readonly GridValidRowModel[],
    setTotalPaymentsDetailsData: Dispatch<SetStateAction<readonly GridValidRowModel[]>>,
    showSnackBar: (message: string, severity: AlertColor) => void,
    setMessageSnack: Dispatch<SetStateAction<string>>;
    setUnauthorized: Dispatch<SetStateAction<boolean>>;
    columnsAuthorizationPayments: GridColDef<PaymentDetail>[],
    setColumnsAuthorizationPayments: Dispatch<SetStateAction<GridColDef<PaymentDetail>[]>>,
    isLoadingData: boolean,
}

export const TableAuthorizationPayment = ({
    resourceScreen,
    setSelectedRows,
    paymentsDetailsData,
    setTotalPaymentsDetailsData,
    setMessageSnack,
    showSnackBar,
    setUnauthorized,
    columnsAuthorizationPayments,
    setColumnsAuthorizationPayments,
    isLoadingData,
}: TableAuthorizationPayment) => {
    const { GenericGetResource } = GenericPromises();
    const {
        LoadColumnsOrder,
        LoadColumnsVisible,
        LoadColumnsSort,
        VerifyPreferenceOrderByResourceRoute,
        VerifyPreferenceVisualByResourceRoute,
        VerifyPreferenceSortByResourceRoute } = useGridPreferences();
    const { preferenceTypes } = usePreferenceTypesStore.getState();
    const [t] = useTranslation("global");
    const [isLoadingDataTable, setIsLoadingDataTable] = useState(false);
    const { setFormatNumberFromTable, setFormatNumber } = useFormatNumber();
    const { ConvertParamsToBase64 } = useParamsFilter();
    const { TimeConverter } = useDates();
    const { LoadExchangeRateHistory } = useCurrencies();
    const [myPreferences, setMyPreferences] = useState({});
    const [mySortPreference, setMySortPreference] = useState([]);
    const { columnsPaymentDetailsAux } = useDialogColumnsAuthorization();

    const onRowSelection = (rowsIds: GridRowSelectionModel) => {
        setSelectedRows((prev) => {
            return paymentsDetailsData.filter(item => rowsIds.includes(item.payment_detail_id));
        });
    }

    const myCustomColumns: GridColDef<any>[] = [
        {
            field: 'purchase_invoice_name', headerName: t("purchaseinvoices.fields.purchase_invoice_name"), headerClassName: 'header-grid-table', flex: 1,
            renderCell(params) {
                let stringDetails = params.row.str_details ?? "";
                let tooltipContent = '';

                if (stringDetails !== "") {
                    const products = stringDetails.split(',');

                    const formattedProducts = products.map((product: any) => {
                        const parts = product.split(' - ');
                        const unit = parseFloat(parts[0]).toFixed(2);
                        const item = parts[1].trim();
                        const price = setFormatNumber({ value: parseFloat(parts[2].replace('$', '')), decimals: 2 });
                        return `${unit} - ${item} - ${price}`;
                    });

                    tooltipContent = formattedProducts.join('\n');
                }
                return (
                    <Tooltip
                        title={
                            <span style={{ whiteSpace: 'pre-line', display: 'inline-block' }}>
                                {tooltipContent}
                            </span>
                        }
                        slotProps={{
                            popper: {
                                sx: {
                                    [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
                                    {
                                        marginTop: '0px',
                                        marginBottom: '0px',
                                        marginLeft: '0px',
                                        marginRight: '0px',
                                        whiteSpace: 'nowrap',
                                        maxWidth: 'none',
                                    },
                                },
                            },
                        }}
                    >
                        <Button onClick={() => {
                            let stringConverted = ConvertParamsToBase64(`purchase_invoice_id=${params.row.purchase_invoice_id}&supplier_id=${params.row.supplier_id}`);
                            window.open(`/purchaseinvoices/view?${stringConverted}`, '_blank');
                        }}>
                            {params.row.purchase_invoice_name}
                        </Button>
                    </Tooltip>
                );
            }
        },
        {
            field: 'project_name', headerName: t("projects.fields.project_name"), headerClassName: 'header-grid-table', flex: 1,
            renderCell(params) {
                return (
                    <Button
                        disabled={(params.row.project_name) ? false : true}
                        onClick={() => {
                            if (params.row.project_id) {
                                let stringConverted = ConvertParamsToBase64(`project_id=${params.row.project_id}`);
                                window.open(`/projects/view?${stringConverted}`, '_blank');
                            }
                        }}
                    >
                        {params.row.project_name}
                    </Button>
                );
            }
        },
        {
            field: 'payment_name', headerName: t("payments.fields.payment_name"), headerClassName: 'header-grid-table', flex: 1,
        },
        {
            field: 'eta', headerName: t("projects.fields.eta"), headerClassName: 'header-grid-table', flex: 1, type: 'date',
            valueGetter(params) {
                return (params.value) ? TimeConverter(params.value) : undefined;
            },
        },
        { field: 'supplier_business_name', headerName: t("purchaseinvoices.fields.supplier_business_name"), headerClassName: 'header-grid-table', flex: 1 },
        { field: 'customer_business_name', headerName: t("projects.fields.customer"), headerClassName: 'header-grid-table', flex: 1 },
        { field: 'shipping_company', headerName: t("projects.fields.shipping_company"), headerClassName: 'header-grid-table', flex: 1 },
        { field: 'related_currency_code', headerName: t("currencies.title-view"), headerClassName: 'header-grid-table', flex: 1 },
        {
            field: 'total_company_currency_amount', headerName: t("companies.configuration.fields.total_company_currency_amount"), headerClassName: 'header-grid-table', flex: 1,
            valueGetter(params) {
                return setFormatNumberFromTable(params.value ?? 0);
            },
        },
        { field: 'user_mail_send', headerName: t("payments.fields.user_mail_send"), headerClassName: 'header-grid-table', flex: 1 },
        {
            field: 'date_mail_send', headerName: t("payments.fields.date_mail_send"), headerClassName: 'header-grid-table', flex: 1, type: 'date',
            valueGetter(params) {
                return (params.value) ? TimeConverter(params.value) : undefined;
            },
        },
    ];

    const loadTablePreferences = async () => {
        // get preferencetypes in Store
        let hasPreferenceOrder: boolean = false;
        // if exists preferencetypes
        if (preferenceTypes.length) {
            // if exists order preferences
            let myPreferencesColumnsOrder = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsOrder");
            if (myPreferencesColumnsOrder) {
                try {
                    const preference = await VerifyPreferenceOrderByResourceRoute(
                        "/authorizationpayments",
                        myPreferencesColumnsOrder.preference_type_id ?? 0
                    );
                    if (preference) {
                        setColumnsAuthorizationPayments(await LoadColumnsOrder("/authorizationpayments", myCustomColumns, preference?.preference_value));
                        hasPreferenceOrder = true;
                    }
                } catch (error) {
                    showSnackBar(`Error fetching column order preferences:${error}`, "error");
                }
            }

            let myPreferencesColumnsVisual = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsVisibility");
            // if exists visual preferences
            if (myPreferencesColumnsVisual) {
                try {
                    const preference = await VerifyPreferenceVisualByResourceRoute(
                        "/authorizationpayments",
                        myPreferencesColumnsVisual.preference_type_id ?? 0
                    );
                    if (preference) {
                        let myLocalPreferences = await LoadColumnsVisible("/authorizationpayments", preference);
                        setMyPreferences(myLocalPreferences);
                    }
                } catch (error) {
                    showSnackBar(`Error fetching column visual preferences:${error}`, "error");
                }
            }

            let myPreferencesColumnsSort = preferenceTypes.find((item: PreferenceType) => item.preference_type_name === "GridColumnsSorting");
            // if exists sort preferences
            if (myPreferencesColumnsSort) {
                try {
                    const preference = await VerifyPreferenceSortByResourceRoute(
                        "/authorizationpayments",
                        myPreferencesColumnsSort.preference_type_id ?? 0
                    );
                    if (preference) {
                        let myLocalPreferences = await LoadColumnsSort("/authorizationpayments", preference);
                        setMySortPreference(myLocalPreferences);
                    }
                } catch (error) {
                    console.error("Error fetching column sort preferences:", error);
                }
            }

        }
        if (!hasPreferenceOrder) {
            setColumnsAuthorizationPayments(columnsPaymentDetailsAux);
        }
    }

    useEffect(() => {
        const loadData = async () => {
            setIsLoadingDataTable(false);
            await loadTablePreferences();

            await LoadExchangeRateHistory()
                .catch((error: unknown) => {
                    if (error instanceof AxiosError) {
                        if (error.response && error.response.status === 400) {
                            showSnackBar(error.response.data, "error");
                        } else {
                            showSnackBar(error.message, "error");
                        }
                    } else {
                        showSnackBar("An unexpected error occurred", "error");
                    }
                });
            GenericGetResource(`/authorizationpayments`)
                .then(async (response) => {
                    setTotalPaymentsDetailsData(response.data.data);
                    setIsLoadingDataTable(true);
                }).catch((error) => {
                    setMessageSnack(error.message);
                    showSnackBar(error.message, "error");
                    setUnauthorized(false);
                });
        }

        if (resourceScreen && resourceScreen.read) {
            loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [resourceScreen]);

    return (
        <>
            <div className="d-flex justify-content-center mt-2 h-100">
                {!isLoadingDataTable && <Spinner />}
                {isLoadingDataTable &&
                    <Box sx={{ height: 'calc(100vh - 360px)', width: '100%' }}>
                        <DataTableSticky
                            isLoading={isLoadingData}
                            columns={columnsAuthorizationPayments}
                            setColumns={setColumnsAuthorizationPayments}
                            data={paymentsDetailsData}
                            entityId={"payment_detail_id"}
                            entity={`AuthorizationPayments`}
                            preferences={myPreferences}
                            namePreferences={"/authorizationpayments"}
                            nameOrderColumns={"/authorizationpayments"}
                            selectRows
                            onRowSelectionModelChange={(rowsIds) => onRowSelection(rowsIds)}
                            isChildren={true}
                            countColumns={[
                                "total_company_currency_amount",
                                "total_tax_amount",
                                "total_retention_amount",
                                "subtotal_amount",
                                "total_amount",
                                "total_company_currency_amount",
                                "payment_amount",
                                "amount_outstanding",
                            ]}
                            sortPreference={mySortPreference}
                        />
                    </Box>
                }
            </div>
        </>
    )
}